import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast, toastErrorMessage } from '../context/ToastContext';
import {
  isOsFileDrag,
  toastCalendarFileDropResult,
  uploadCalendarDayFiles,
  type CalendarFileDropResult,
  type CalendarUploadProgress,
} from '../utils/calendarFileDrop';

interface UseCalendarDayFileDropOptions {
  userId: number | null | undefined;
  /**
   * Calendar day for health-document hint, or null/omit for auto-detect (LLM / file date).
   */
  testDateHint?: string | null;
  onComplete?: (result: CalendarFileDropResult) => void | Promise<void>;
}

/** Drag-and-drop handlers for FIT/WSE/PDF/image onto a calendar day or auto-detect zone. */
export function useCalendarDayFileDrop({
  userId,
  testDateHint,
  onComplete,
}: UseCalendarDayFileDropOptions) {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const [isFileDragOver, setIsFileDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<CalendarUploadProgress | null>(null);

  const processFiles = useCallback(
    async (files: File[], dateHintOverride?: string | null) => {
      if (!files.length) return;
      if (userId == null) {
        showToast(t('calendar.toast.uploadNeedAthlete'), 'info');
        return;
      }

      setIsFileDragOver(false);
      setUploading(true);
      setUploadProgress({ phase: 'uploading', uploadPercent: 0 });
      try {
        const result = await uploadCalendarDayFiles(files, {
          userId,
          testDateHint:
            dateHintOverride !== undefined ? dateHintOverride ?? undefined : testDateHint ?? undefined,
          locale: i18n.language?.split('-')[0],
          onProgress: setUploadProgress,
        });

        toastCalendarFileDropResult(result, t, (message, variant) => showToast(message, variant));

        const hadSuccess =
          result.activityImportedCount + result.healthSavedCount + result.healthPartialCount > 0;
        if (hadSuccess) {
          await onComplete?.(result);
        }
      } catch (err) {
        const msg =
          err instanceof Error && err.message === 'too_many_activity_files'
            ? t('calendar.toast.tooManyFiles')
            : toastErrorMessage(err, t('calendar.toast.importFailed'));
        showToast(msg);
      } finally {
        setUploading(false);
        setUploadProgress(null);
      }
    },
    [i18n.language, onComplete, showToast, t, testDateHint, userId],
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (!isOsFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsFileDragOver(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsFileDragOver(false);
    }
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsFileDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length) void processFiles(files);
    },
    [processFiles],
  );

  return {
    uploading,
    uploadProgress,
    isFileDragOver,
    onDragOver,
    onDragLeave,
    onDrop,
    processFiles,
  };
}
